import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';

import type { Metadata } from 'hb-react/frontoffice/apps/Forum/types';
import {
  isFileAnImage,
  isFileAVideo,
} from 'hb-react/shared/utils/files/fileContentTypes';

interface AttachmentProps {
  metadata: Metadata;
  download_url: string;
  thumbnail_url: string | null;
}

const Attachment = ({
  download_url: downloadUrl,
  thumbnail_url: thumbnailUrl,
  metadata,
}: AttachmentProps) => (
  <Box display="block">
    <BoxStyled>
      <Preview thumbnailUrl={thumbnailUrl} contentType={metadata.mime_type} />
    </BoxStyled>
    <Box pt={3} justifyContent="space-between" alignItems="center">
      <Box display="block" font="body-semibold-asphalt" pr={1} maxWidth="90%">
        <Tooltip
          initialPosition={POSITIONS.BOTTOM}
          childrenElement={metadata.filename}
          hideOnScreenReader
        >
          <Dotdotdot clamp={1} useNativeClamp>
            {metadata.filename}
          </Dotdotdot>
        </Tooltip>
      </Box>
      <Box as="a" href={downloadUrl} download data-testid="file-download">
        <FontAwesomeIcon
          iconName="arrow-down-to-line"
          iconType={CATEGORIES.REGULAR}
          height="18px"
          color="asphalt"
        />
      </Box>
    </Box>
  </Box>
);

interface PreviewProps {
  thumbnailUrl: string | null;
  contentType: string;
}

const Preview = ({ thumbnailUrl, contentType }: PreviewProps) => {
  if (thumbnailUrl && isFileAnImage(thumbnailUrl, contentType)) {
    return <ImgStyled src={thumbnailUrl} data-testid="image-attachment" />;
  }

  if (isFileAVideo(contentType)) {
    return (
      <OtherStyled data-testid="video-attachment">
        <FontAwesomeIcon
          iconName="circle-play"
          iconType={CATEGORIES.DUOTONE}
          height="50px"
          color="primary"
        />
      </OtherStyled>
    );
  }

  return (
    <OtherStyled data-testid="file-attachment">
      <FontAwesomeIcon
        iconName="file-lines"
        iconType={CATEGORIES.DUOTONE}
        height="50px"
        color="primary"
      />
    </OtherStyled>
  );
};

export default Attachment;

const BoxStyled = styled(Box).attrs({
  display: 'block',
  width: '100%',
  height: '135px',
})``;

const ImgStyled = styled(Image).attrs({
  display: 'block',
  height: '100%',
  width: '100%',
})`
  object-fit: cover;
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;

const OtherStyled = styled(Box).attrs(({ theme }) => ({
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  color: `${theme.colors.primary}`,
  bg: `${theme.colors.white}`,
}))`
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
  border: ${({ theme }) => `1px solid ${theme.colors.horizon}`};
`;
